/**
 * Cash Flow hub page registry — Access `frmReportCashFlowMain` "Go to Page"
 * combo (cbPreferencesList) + per-page toolbar config (cbPreferencesList_Change).
 *
 * Each page maps to a list view + the toolbar buttons the Access form shows for
 * that page. Button captions/visibility copied verbatim from the VBA.
 */
import type { CashflowColumn } from './CashflowDataList'

export type CashflowPageKey =
  | 'load_on_board'
  | 'project_new_p'
  | 'project_new_npe'
  | 'others'
  | 'bank'
  | 'cp'
  | 'service'
  | 'muahang'
  | 'banhang'

/** Toolbar action ids → dialog the Frame opens. */
export type CashflowAction =
  | 'input'
  | 'cp-edit'
  | 'cp-need'
  | 'cp-dup'
  | 'cp-misa'
  | 'svc-edit'
  | 'svc-need'
  | 'svc-dup'
  | 'svc-misa'
  | 'mh-new'
  | 'mh-edit'
  | 'mh-delete'
  | 'mh-need'
  | 'mh-misa'
  | 'bh-new'
  | 'bh-edit'
  | 'bh-delete'
  | 'bh-need'
  | 'bh-misa'

export interface ToolbarButtonCfg {
  action: CashflowAction
  /** Short display label (shown on the button). */
  label: string
  /** Full Access caption — used as tooltip when `label` is abbreviated. */
  title?: string
  color: 'success' | 'warning' | 'primary' | 'error' | 'secondary'
  icon: 'new' | 'edit' | 'delete' | 'list' | 'value'
  rowRequired: boolean
}

export interface CashflowPageCfg {
  key: CashflowPageKey
  label: string
  group: 'Input' | 'Report' | 'Mapping'
  buttons: ToolbarButtonCfg[]
}

/** Combo options in Access RowSource order. */
export const CASHFLOW_PAGES: CashflowPageCfg[] = [
  {
    key: 'load_on_board',
    label: 'Input - Loading on Board',
    group: 'Input',
    buttons: [{ action: 'input', label: 'Input', title: 'Input Value', color: 'success', icon: 'value', rowRequired: false }],
  },
  {
    key: 'project_new_p',
    label: 'Input - Projected New Purchases',
    group: 'Input',
    buttons: [{ action: 'input', label: 'Input', title: 'Input Value', color: 'success', icon: 'value', rowRequired: false }],
  },
  {
    key: 'project_new_npe',
    label: 'Input - Projected New Consignment Pricing',
    group: 'Input',
    buttons: [{ action: 'input', label: 'Input', title: 'Input Value', color: 'success', icon: 'value', rowRequired: false }],
  },
  {
    key: 'others',
    label: 'Input - Others',
    group: 'Input',
    // rowRequired: each Others row IS its own input_type → must pick a row so the
    // save targets the right input_type (not a blank key).
    buttons: [{ action: 'input', label: 'Input', title: 'Input Value', color: 'success', icon: 'value', rowRequired: true }],
  },
  { key: 'bank', label: 'Report - Balance Bank Account', group: 'Report', buttons: [] },
  {
    key: 'cp',
    label: 'Customer/Vender Mapping',
    group: 'Mapping',
    buttons: [
      { action: 'cp-edit', label: 'Edit', title: 'Edit - Mapping', color: 'warning', icon: 'edit', rowRequired: true },
      { action: 'cp-need', label: 'Need Map', title: 'Need to map', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'cp-dup', label: 'Duplicate', title: 'Duplicate Data', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'cp-misa', label: 'Misa CP', title: 'Misa Vender/Customer', color: 'primary', icon: 'list', rowRequired: false },
    ],
  },
  {
    key: 'service',
    label: 'Service Mapping',
    group: 'Mapping',
    buttons: [
      { action: 'svc-edit', label: 'Edit', title: 'Edit - Mapping', color: 'warning', icon: 'edit', rowRequired: true },
      { action: 'svc-need', label: 'Need Map', title: 'Need to map', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'svc-dup', label: 'Duplicate', title: 'Duplicate Data', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'svc-misa', label: 'Misa CP', title: 'Misa Vender/Customer', color: 'primary', icon: 'list', rowRequired: false },
    ],
  },
  {
    key: 'muahang',
    label: 'Purchase - Invoice Mapping',
    group: 'Mapping',
    buttons: [
      { action: 'mh-new', label: 'New', title: 'New - Mapping', color: 'success', icon: 'new', rowRequired: false },
      { action: 'mh-edit', label: 'Edit', title: 'Edit - Mapping', color: 'warning', icon: 'edit', rowRequired: true },
      { action: 'mh-delete', label: 'Delete', title: 'Delete - Mapping', color: 'error', icon: 'delete', rowRequired: true },
      { action: 'mh-need', label: 'Need Map', title: 'Need to map', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'mh-misa', label: 'Misa Purchase', title: 'Misa Purchase Invoices', color: 'primary', icon: 'list', rowRequired: false },
    ],
  },
  {
    key: 'banhang',
    label: 'Sale - Invoice Mapping',
    group: 'Mapping',
    buttons: [
      { action: 'bh-new', label: 'New', title: 'New - Mapping', color: 'success', icon: 'new', rowRequired: false },
      { action: 'bh-edit', label: 'Edit', title: 'Edit - Mapping', color: 'warning', icon: 'edit', rowRequired: true },
      { action: 'bh-delete', label: 'Delete', title: 'Delete - Mapping', color: 'error', icon: 'delete', rowRequired: true },
      { action: 'bh-need', label: 'Need Map', title: 'Need to map', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'bh-misa', label: 'Misa Sales', title: 'Misa Sales Invoices', color: 'primary', icon: 'list', rowRequired: false },
    ],
  },
]

// ── Column definitions per list (Access ColumnOrder; captions verbatim). ──
export const BANK_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'bankAccountNumber', label: 'Bank Number' },
  { key: 'bankName', label: 'Bank Name' , wrap: true, width: 180 },
  { key: 'openAmountOc', label: 'Open (Raw)', kind: 'number', align: 'right' },
  { key: 'openAmount', label: 'Open (VND)', kind: 'number', align: 'right' },
  { key: 'debitAmountOc', label: 'Debit (Raw)', kind: 'number', align: 'right' },
  { key: 'debitAmount', label: 'Debit (VND)', kind: 'number', align: 'right' },
  { key: 'creditAmountOc', label: 'Credit (Raw)', kind: 'number', align: 'right' },
  { key: 'creditAmount', label: 'Credit (VND)', kind: 'number', align: 'right' },
  { key: 'closeAmountOc', label: 'Close (Raw)', kind: 'number', align: 'right' },
  { key: 'closeAmount', label: 'Close (VND)', kind: 'number', align: 'right' },
  { key: 'bankFx', label: 'Bank FX', kind: 'number', align: 'right' },
  { key: 'bankBranchName', label: 'Bank Branch Name' , wrap: true, width: 180 },
  { key: 'isSudung', label: 'Is Used?', filter: 'checkboxes' },
  { key: 'description', label: 'Description' , wrap: true, width: 220 },
  { key: 'address', label: 'Address' , wrap: true, width: 220 },
]

export const CP_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'isMap', label: 'IsMapped?', filter: 'checkboxes' },
  { key: 'erpCode', label: 'ERP - Code' },
  { key: 'erpShortName', label: 'ERP - ShortName' , wrap: true, width: 180 },
  { key: 'erpName', label: 'ERP - Name' , wrap: true, width: 200 },
  { key: 'erpVat', label: 'ERP - VATCode' },
  { key: 'misaCode', label: 'MISA - Code' },
  { key: 'misaName', label: 'MISA - Name' , wrap: true, width: 200 },
  { key: 'misaVat', label: 'MISA - VATCode' },
  { key: 'isVender', label: 'MISA - IsVender?', filter: 'checkboxes' },
  { key: 'isCustomer', label: 'MISA - IsCustomer?', filter: 'checkboxes' },
  { key: 'isEmployee', label: 'MISA - IsEmployee?', filter: 'checkboxes' },
  { key: 'hadPcontract', label: 'ERP - Had P contract?', filter: 'checkboxes' },
  { key: 'hadScontract', label: 'ERP - Had S contract?', filter: 'checkboxes' },
]

export const CP_NEED_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'checkType', label: 'Check Type', filter: 'checkboxes' },
  ...CP_COLUMNS,
  { key: 'misaLstInvoice', label: 'MISA - Lst Invoice' , wrap: true, width: 200 },
  { key: 'misaNgoaile', label: 'MISA - Ngoai le' },
]

export const SERVICE_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'isMapped', label: 'IsMapped?', filter: 'checkboxes' },
  { key: 'serviceCode', label: 'ServiceCode' },
  { key: 'serviceTypeName', label: 'ServiceType' , wrap: true, width: 160 },
  { key: 'serviceName', label: 'ServiceName' , wrap: true, width: 200 },
  { key: 'misaCode', label: 'MisaCode' },
  { key: 'misaName', label: 'MisaName' , wrap: true, width: 200 },
  { key: 'misaVatCode', label: 'MisaVATCode' },
  { key: 'tlcPic', label: 'Service PIC' },
  { key: 'tel', label: 'Service Tel' },
  { key: 'misaAddress', label: 'MisaAddress' , wrap: true, width: 200 },
  { key: 'address', label: 'Service Address' , wrap: true, width: 220 },
  { key: 'taxNo', label: 'Service TaxNo' },
  { key: 'note', label: 'Service Note' , wrap: true, width: 220 },
]

export const MISA_OBJECT_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'misaCode', label: 'MISA - Code' },
  { key: 'misaName', label: 'MISA - Name' , wrap: true, width: 200 },
  { key: 'misaAddress', label: 'MISA - Address' , wrap: true, width: 200 },
  { key: 'misaVatCode', label: 'MISA - VATCode' },
  { key: 'mappedTxt', label: 'Mapped?' },
  { key: 'erpCode', label: 'ERP - Code' },
  { key: 'erpName', label: 'ERP - Name' , wrap: true, width: 200 },
]

export const MUAHANG_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'mappedTxt', label: 'Is Mapped?', filter: 'checkboxes' },
  { key: 'sohoadonCheckdate', label: 'ERP - Check Date', kind: 'date' },
  { key: 'erpCp', label: 'ERP - CP Code' },
  { key: 'erpMisaCode', label: 'ERP - Misa Code' },
  { key: 'erpTabName', label: 'ERP - Tab Name' },
  { key: 'erpInvNo', label: 'ERP - Invoice No' },
  { key: 'invoiceDate', label: 'ERP - Invoice Date', kind: 'date' },
  { key: 'invoiceAmount', label: 'ERP - Invoice Amount', kind: 'number', align: 'right' },
  { key: 'erpInvNote', label: 'ERP - Invoice Note' , wrap: true, width: 200 },
  { key: 'tabName', label: 'Misa - Tab Name' },
  { key: 'nhacungcap', label: 'Misa - Vender' , wrap: true, width: 200 },
  { key: 'sochungtu', label: 'Misa - Vourcher Number' },
  { key: 'sohoadon', label: 'Misa - Invoice Number' },
  { key: 'ngayhoadon', label: 'Misa - Invoice Date', kind: 'date' },
  { key: 'thanhtienQuydoi', label: 'Misa - Amount (Convert)', kind: 'number', align: 'right' },
  { key: 'thanhtoanQuydoi', label: 'Misa - Payment (Convert)', kind: 'number', align: 'right' },
  { key: 'loaitien', label: 'Misa - Currency' },
  { key: 'tygia', label: 'Misa - Exchange Rate', kind: 'number', align: 'right' },
  { key: 'refid', label: 'Misa - Refid' },
  { key: 'paydataRequested', label: 'ERP - Payment Requested', kind: 'number', align: 'right' },
  { key: 'paydataPaid', label: 'ERP - Payment Paid', kind: 'number', align: 'right' },
  { key: 'contractNo', label: 'ERP - Contract No' },
]

export const BANHANG_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'isMapped', label: 'IsMapped?', filter: 'checkboxes' },
  { key: 'contractNo', label: 'ERP-ContractNo' },
  { key: 'contractDate', label: 'ERP-ContractDate', kind: 'date' },
  { key: 'erpInv', label: 'ERP-InvNo' },
  { key: 'buyersRef', label: 'ERP-BuyersRef' },
  { key: 'cpCode', label: 'ERP-CpCode' },
  { key: 'shortName', label: 'ERP-CpName' , wrap: true, width: 180 },
  { key: 'accountObjectCode', label: 'MISA-ObjectCode' },
  { key: 'tabName', label: 'MISA-Type' },
  { key: 'sochungtu', label: 'MISA-VoucherNo' },
  { key: 'misaContract', label: 'MISA-ContractNo' },
  { key: 'contractExtract', label: 'MISA-ContractExtract' , wrap: true, width: 200 },
  { key: 'invExtract', label: 'MISA-InvExtract' , wrap: true, width: 200 },
  { key: 'misaSohoadon', label: 'MISA-InvNo' },
  { key: 'dienGiai', label: 'MISA-Note' , wrap: true, width: 200 },
  { key: 'contractType', label: 'ERP-ContractType' },
]

export const BANHANG_DIFF_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'customsInvNo', label: 'Customs Inv No' },
  { key: 'contractNo', label: 'Contract No' },
  { key: 'erpKhachhang', label: 'ERP - Customer' , wrap: true, width: 180 },
  { key: 'misaLstCt', label: 'MISA - Lst Contract' , wrap: true, width: 200 },
  { key: 'misaKhachhang', label: 'MISA - Customer' , wrap: true, width: 180 },
]
